import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { libraryApi } from '../../lib/api/libraryApi';

/**
 * Before a library AI interview edit is saved, tells the recruiter which live
 * screenings it will update. Used by both the standard and the rubric editor.
 *
 *   const { requestSave, confirmModal, reportSyncResult } = useLibraryEditImpactConfirm(templateId);
 *   onClick={() => requestSave(doSave)}   ...   {confirmModal}
 */
export default function useLibraryEditImpactConfirm(templateId) {
  const [pending, setPending] = useState(null);
  const saveRef = useRef(null);

  const requestSave = useCallback(
    async (save) => {
      if (!templateId) {
        save();
        return;
      }
      saveRef.current = save;
      try {
        const impact = await libraryApi.getLibraryAIInterviewImpact(templateId);
        if (!impact?.linkedScreenings) {
          saveRef.current = null;
          save();
          return;
        }
        setPending(impact);
      } catch {
        // Could not count — still warn rather than save silently.
        setPending({ linkedScreenings: null, sessionsInProgress: 0 });
      }
    },
    [templateId]
  );

  const close = useCallback(() => {
    setPending(null);
    saveRef.current = null;
  }, []);

  const confirm = useCallback(() => {
    const save = saveRef.current;
    close();
    if (save) save();
  }, [close]);

  const count = pending?.linkedScreenings;
  const screeningsText =
    count == null
      ? 'the screenings that use this interview'
      : `${count} screening${count === 1 ? '' : 's'} that use${count === 1 ? 's' : ''} this interview`;
  const inProgressText = pending?.sessionsInProgress
    ? ` ${pending.sessionsInProgress} candidate${pending.sessionsInProgress === 1 ? ' is' : 's are'} mid-interview right now and will not be affected.`
    : '';

  const confirmModal = (
    <ConfirmModal
      isOpen={Boolean(pending)}
      onClose={close}
      onConfirm={confirm}
      title="Update linked screenings?"
      message={
        `Saving will also update ${screeningsText}. ` +
        'Candidates invited from now on get the new version; candidates already invited keep the version they were invited with.' +
        inProgressText
      }
      confirmLabel="Save and update"
      cancelLabel="Keep editing"
      variant="warning"
    />
  );

  const reportSyncResult = useCallback((data) => {
    const failed = data?.sync?.failed || 0;
    if (failed > 0) {
      toast.error(
        `Saved to the library, but ${failed} linked screening${failed === 1 ? '' : 's'} could not be updated. Please contact support.`
      );
      return false;
    }
    return true;
  }, []);

  return { requestSave, confirmModal, reportSyncResult };
}
